import { FormEvent, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createInvitation, InviteCreateResp } from "../../lib/api";
import { InviteResult } from "./InviteResult";

/** Reusable "invite the next role down" form.
 *
 * The backend enforces the cascade (who can invite whom into which
 * scope); this component collects either an email or a phone number
 * and fires the request for a fixed (role, scopeType, scopeId) triple
 * decided by the parent page.
 *
 * Two channels:
 *   - Email: the invitee gets a link to set a password and join.
 *   - Phone: passwordless — the invitee signs in with their number
 *     (one-time code) and their account is created on first verify.
 * On success it shows delivery status plus, for email, a copy-able
 * accept link so onboarding works even when email is down. */
export function InviteForm({
  role,
  roleLabel,
  scopeType,
  scopeId,
  invalidateKey,
}: {
  role: string;
  roleLabel: string;
  scopeType: "enterprise" | "state" | "district" | "godown";
  scopeId: number;
  /** react-query key to invalidate after a successful invite so a
   * sibling "pending invitations" list refreshes. Optional. */
  invalidateKey?: unknown[];
}) {
  const qc = useQueryClient();
  const [channel, setChannel] = useState<"email" | "phone">("email");
  const [value, setValue] = useState("");
  const [result, setResult] = useState<InviteCreateResp | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      createInvitation({
        ...(channel === "email"
          ? { email: value.trim() }
          : { phone: value.trim() }),
        role,
        target_scope_type: scopeType,
        target_scope_id: scopeId,
      }),
    onSuccess: (resp: InviteCreateResp) => {
      setValue("");
      setResult(resp);
      if (invalidateKey) qc.invalidateQueries({ queryKey: invalidateKey });
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!value.trim() || mut.isPending) return;
    setResult(null);
    mut.mutate();
  };

  const switchChannel = (c: "email" | "phone") => {
    setChannel(c);
    setValue("");
    setResult(null);
  };

  return (
    <div className="bg-white rounded shadow p-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-1">
        Invite {roleLabel}
      </h3>
      <p className="text-xs text-gray-500 mb-3">
        {channel === "email"
          ? "They'll get an email with a link to set a password and join — and you'll get a copy-able link here as a fallback."
          : "They'll sign in with their phone number using a one-time code — no password needed."}
      </p>

      {/* Channel switch. */}
      <div className="flex rounded border overflow-hidden text-xs mb-2 w-40">
        <button
          type="button"
          onClick={() => switchChannel("email")}
          className={`flex-1 py-1 font-medium ${
            channel === "email"
              ? "bg-agrilens-green text-white"
              : "bg-white text-gray-600 hover:bg-gray-50"
          }`}
        >
          Email
        </button>
        <button
          type="button"
          onClick={() => switchChannel("phone")}
          className={`flex-1 py-1 font-medium ${
            channel === "phone"
              ? "bg-agrilens-green text-white"
              : "bg-white text-gray-600 hover:bg-gray-50"
          }`}
        >
          Phone
        </button>
      </div>

      <form onSubmit={onSubmit} className="flex flex-col gap-2">
        <input
          type={channel === "email" ? "email" : "tel"}
          required
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={
            channel === "email" ? "name@example.com" : "+91 98765 43210"
          }
          className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-agrilens-green"
        />
        <button
          type="submit"
          disabled={mut.isPending}
          className="bg-agrilens-green text-white text-sm font-medium py-2 rounded hover:bg-agrilens-green-dark disabled:opacity-50"
        >
          {mut.isPending ? "Sending…" : "Send invite"}
        </button>
      </form>

      {mut.isError && (
        <div className="mt-2 text-xs text-red-700 bg-red-50 rounded p-2">
          {(mut.error as Error).message}
        </div>
      )}
      {result && <InviteResult resp={result} />}
    </div>
  );
}
